// Environment-based S3 and CloudFront configuration
export const S3_BUCKET_NAME =
  process.env.REACT_APP_S3_BUCKET_NAME || "landing-page-app-hero-images";
export const CLOUDFRONT_DOMAIN =
  process.env.REACT_APP_CLOUDFRONT_DOMAIN || "d3p2r6ofnvoe67.cloudfront.net";
export const S3_BASE_URL = `https://${S3_BUCKET_NAME}.s3.amazonaws.com`;
export const S3_MEDIA_URL = `${S3_BASE_URL}/media`;
export const CLOUDFRONT_BASE_URL = `https://${CLOUDFRONT_DOMAIN}`;

// Helper: Build complete S3 URL from filename
export const buildS3MediaUrl = (filename: string): string =>
  `${S3_MEDIA_URL}/${filename}`;

// Helper: Check if URL is an S3 media URL (bucket + path specific).
// For general "is this any S3 URL?" detection, use isS3Url from isS3Url.ts instead.
export const isS3MediaUrl = (url: string): boolean => {
  return (
    url.includes(`${S3_BUCKET_NAME}.s3.amazonaws.com/media/`) ||
    url.includes("landing-page-app-hero-images.s3.amazonaws.com/media/")
  );
};

// Helper: Check if value should be transformed to CloudFront URL
// Returns true for S3 URLs and bare filenames, false for other full URLs
export const shouldTransformToCloudFront = (value: string): boolean => {
  // If it's an S3 URL, transform it
  if (isS3MediaUrl(value)) {
    return true;
  }
  // If it's a full URL (http/https) but not S3, don't transform
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return false;
  }
  // It's a bare filename - transform it
  return true;
};

// Helper: Extract filename from S3 URL (handles both http and https)
export const extractMediaFilename = (s3Url: string): string => {
  const patterns = [
    `https://${S3_BUCKET_NAME}.s3.amazonaws.com/media/`,
    `http://${S3_BUCKET_NAME}.s3.amazonaws.com/media/`,
    // Legacy prod bucket pattern for existing data
    "https://landing-page-app-hero-images.s3.amazonaws.com/media/",
    "http://landing-page-app-hero-images.s3.amazonaws.com/media/",
  ];
  for (const pattern of patterns) {
    if (s3Url.includes(pattern)) {
      return s3Url.split(pattern)[1] || "";
    }
  }
  return s3Url;
};

// Helper: Build CloudFront URL with image transformations
export const buildCloudFrontUrl = (
  filename: string,
  options: { width?: number; height?: number; quality?: number } = {}
): string => {
  const { width = 500, height = 500, quality = 60 } = options;
  return `${CLOUDFRONT_BASE_URL}/fit-in/${height}x${width}/filters:quality(${quality})/filters:strip_exif()/filters:format(webp)/media/${filename}`;
};

// Helper: Get the display URL for a photo
// - Facebook CDN URLs: use as-is
// - S3 URLs or bare filenames: transform to CloudFront
// - Other URLs: use as-is
export const getPhotoDisplayUrl = (
  photoUrl: string | undefined,
  options: { width?: number; height?: number; quality?: number } = {},
  fallback = "/prophone_favicon_square.png"
): string => {
  if (!photoUrl) {
    return fallback;
  }

  // Facebook CDN - use as-is
  if (photoUrl.includes("fbcdn.net")) {
    return photoUrl;
  }

  // S3 URLs or bare filenames - transform to CloudFront
  if (shouldTransformToCloudFront(photoUrl)) {
    return buildCloudFrontUrl(extractMediaFilename(photoUrl), options);
  }

  // Other URLs (external, already CloudFront, etc.) - use as-is
  return photoUrl;
};
